import { isAxiosError, HttpStatusCode } from "axios";

/** 許可IP外アクセスをバックエンドが示すエラーコード */
export const IP_NOT_ALLOWED_CODE = "ip_not_allowed";

/** 許可IP外アクセス時に表示する専用画面のパス */
export const IP_NOT_ALLOWED_PATH = "/ip-not-allowed";

/** 許可IP外と判定された状態を保持するlocalStorageのキー */
const IP_NOT_ALLOWED_STORAGE_KEY = "ipNotAllowed";

interface IIpRestrictionErrorResponse {
  code?: string;
}

/**
 * 許可IP外アクセスによるエラーかどうかを判定する
 *
 * 403は閲覧専用ユーザーの拒否などでも返るため、ステータスコードだけでは判別できない。
 * バックエンドが付与する `code` まで見て、IP制限だけを拾う。
 */
export const isIpNotAllowedError = (error: unknown): boolean => {
  if (!isAxiosError<IIpRestrictionErrorResponse>(error)) return false;
  if (error.response?.status !== HttpStatusCode.Forbidden) return false;

  return error.response.data?.code === IP_NOT_ALLOWED_CODE;
};

/**
 * バイナリで受け取ったエラーレスポンスをJSONへ復元する
 *
 * `responseType: "blob"` / `"arraybuffer"` を指定したリクエストでは、axiosの
 * transformResponse がJSONへパースしないため、403のボディが Blob / ArrayBuffer の
 * まま届き `code` を読めない。判定の前にオブジェクトへ戻す。
 *
 * ファイルダウンロードや地図タイルの取得がこの経路のため、復元しないと
 * それらのリクエストで拒否されても専用画面へ誘導できない。
 */
export const restoreBinaryErrorResponse = async (
  error: unknown
): Promise<void> => {
  if (!isAxiosError(error) || !error.response) return;

  const data: unknown = error.response.data;
  // instanceof はレルムをまたぐと一致しないため、内部タグで判定する
  const tag = Object.prototype.toString.call(data);
  let text: string;

  if (tag === "[object Blob]") {
    text = await (data as Blob).text();
  } else if (tag === "[object ArrayBuffer]") {
    text = new TextDecoder().decode(data as ArrayBuffer);
  } else {
    return;
  }

  try {
    const parsed: unknown = JSON.parse(text);
    // 配列やプリミティブで元のボディを置き換えると、呼び出し側のエラー処理が
    // 想定しない形になる。オブジェクトの場合のみ差し替える
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      error.response.data = parsed;
    }
  } catch {
    // JSONでないボディはそのままにする（判定側でcodeが読めず対象外になる）
  }
};

/**
 * 許可IP外と判定された状態を記録する
 *
 * ログイン状態を保持する仕様のため、記録が無いとブラウザバックやブックマークから
 * アプリ画面を描画できてしまう。
 *
 * sessionStorageではタブ単位になり、別タブを開くと記録が無いままアプリ画面が
 * 描画されてしまうため、タブ間で共有されるlocalStorageに保持する。
 * ブラウザを閉じても残るが、認証クッキーはセッションクッキーのため
 * 再起動後は未ログインとなり、`IpRestrictionGuard` は遮断しない。
 */
export const markIpNotAllowed = (): void => {
  try {
    localStorage.setItem(IP_NOT_ALLOWED_STORAGE_KEY, "true");
  } catch (error) {
    // サイトデータがブロックされた環境では例外になる。ここで落とすと呼び出し元の
    // 専用画面への遷移まで巻き添えになるため、記録できないことは許容する
    console.log(error);
  }
};

/** 許可IP外と判定された状態を解除する */
export const clearIpNotAllowed = (): void => {
  try {
    localStorage.removeItem(IP_NOT_ALLOWED_STORAGE_KEY);
  } catch (error) {
    console.log(error);
  }
};

/** 許可IP外と判定された状態かどうかを返す */
export const isIpNotAllowedState = (): boolean => {
  try {
    return localStorage.getItem(IP_NOT_ALLOWED_STORAGE_KEY) === "true";
  } catch (error) {
    // 描画中にも呼ばれるため、読めない場合は遮断しない側に倒す
    console.log(error);
    return false;
  }
};

/**
 * アプリのヘッダー・サイドメニューを隠すべきかどうかを返す
 *
 * ヘッダーとサイドメニューは `IpRestrictionGuard` の外側にあるため、ガードが
 * 遷移させるより先に一度描画される。別タブやURL直打ちでは初回描画の時点では
 * まだ `/ip-not-allowed` に居ないため、現在のパスだけで判定すると
 * ヘッダーとサイドメニューが1フレームだけ見えてしまう。
 * 拒否状態そのものも見て、初回描画から隠す。
 */
export const shouldHideAppNavigation = (pathname: string): boolean =>
  pathname === IP_NOT_ALLOWED_PATH || isIpNotAllowedState();
